import React, { useState } from 'react';
import { Box, Button, Typography, Paper, Alert, CircularProgress } from '@mui/material';
import { testCors, testImageUpload } from '../utils/corsTest';

const CorsTest: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const handleTest = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await testCors();
            setResult(response);

            if (!response.success) {
                setError(response.message || 'CORS test failed');
            }
        } catch (err: any) {
            setError(err.message || 'An error occurred during the CORS test');
            console.error('CORS test error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            setSelectedFile(event.target.files[0]);
        }
    };

    const handleImageUploadTest = async () => {
        if (!selectedFile) {
            setError('Please select a file first');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await testImageUpload(selectedFile);
            setResult(response);

            if (!response.success) {
                setError(response.message || 'Image upload test failed');
            }
        } catch (err: any) {
            setError(err.message || 'An error occurred during the image upload test');
            console.error('Image upload test error:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper elevation={3} sx={{ p: 3, m: 2, maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h5" gutterBottom>
                CORS Configuration Test
            </Typography>

            <Box sx={{ mb: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleTest}
                    disabled={loading}
                    sx={{ mr: 2 }}
                >
                    Test CORS Setup
                </Button>

                <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="raised-button-file"
                    type="file"
                    onChange={handleFileChange}
                />
                <label htmlFor="raised-button-file">
                    <Button variant="outlined" component="span" sx={{ mr: 2 }}>
                        Select Image
                    </Button>
                </label>

                {selectedFile && (
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleImageUploadTest}
                        disabled={loading}
                    >
                        Test Image Upload
                    </Button>
                )}
            </Box>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ my: 2 }}>
                    {error}
                </Alert>
            )}

            {result && result.success && (
                <Alert severity="success" sx={{ my: 2 }}>
                    {result.message}
                </Alert>
            )}

            {result && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, overflow: 'auto' }}>
                    <Typography variant="subtitle2">Test Result:</Typography>
                    <pre>{JSON.stringify(result, null, 2)}</pre>
                </Box>
            )}
        </Paper>
    );
};

export default CorsTest; 